import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import { FaChartLine, FaBuilding, FaCoins } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { RootState } from 'app/store';
import SellIncomeAssets from './SellIncomeAssets';
import SellGrowthAssets from './SellGrowthAssets';

interface AssetTypeData {
  id: string;
  name: string;
  totalAssets: number;
  totalValue: number;
  description: string;
  type: 'incomeUnits' | 'growthUnits';
  gradient: string;
  icon: React.ReactNode;
  available: boolean;
  features: string[];
  riskLevel: string;
}

interface PrefilledAsset {
  assetId: string;
  assetName: string;
  assetType: string;
  currentValue: number;
  purchaseValue: number;
  purchaseDate?: string;
  subType?: string;
}

interface LocationState {
  prefilledAsset?: PrefilledAsset;
  unitType?: 'incomeUnits' | 'growthUnits';
  fromAssetList?: boolean;
}

const SellAssets: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get location state
  const locationState = location.state as LocationState;

  // Selected asset type state
  const [selectedAssetType, setSelectedAssetType] = useState<'incomeUnits' | 'growthUnits' | null>(
    locationState?.unitType || null
  );

  const selectedProjectId = useAppSelector(
    (state) => state.homeTilesServices.selectedProjectId || ''
  );

  const [loggedInUser, setLoggedInUser] = useState(() => {
    const loginInfo = localStorage.getItem('user-details');
    if (loginInfo) {
      try {
        const userDetails = JSON.parse(loginInfo);
        return {
          name: userDetails.name || '',
          email: userDetails.email || '',
          avatar: userDetails.avatar || ''
        };
      } catch (error) {
        console.error('Error parsing user details:', error);
        return { name: '', email: '', avatar: '' };
      }
    }
    return { name: '', email: '', avatar: '' };
  });

  const getGroupId = () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
      projectId?: string;
    };
    return selectedProject?.projectId || '';
  };

  // Mock asset data - replace with actual Redux state
  const assetTypeData = useMemo((): AssetTypeData[] => {
    return [
      {
        id: 'income-assets',
        name: 'Income Unit Assets',
        totalAssets: 5,
        totalValue: 250000,
        description: 'Sell assets from your income units portfolio',
        type: 'incomeUnits',
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        icon: <FaCoins size={48} />,
        available: true,
        features: [
          'Conservative investments',
          'Stable returns',
          'Lower risk profile',
          'Regular income generation'
        ],
        riskLevel: 'Conservative'
      },
      {
        id: 'growth-assets',
        name: 'Growth Unit Assets',
        totalAssets: 8,
        totalValue: 500000,
        description: 'Sell assets from your growth units portfolio',
        type: 'growthUnits',
        gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        icon: <FaChartLine size={48} />,
        available: true,
        features: [
          'Capital appreciation focus',
          'Higher growth potential',
          'Diversified portfolio',
          'Long-term wealth building'
        ],
        riskLevel: 'Moderate to High'
      }
    ];
  }, []);

  const formatCurrency = (amount: number) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '$0';
    }

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;
    let formattedAmount;

    if (absAmount >= 1000000000) {
      formattedAmount = (absAmount / 1000000000).toFixed(3) + 'B';
    } else if (absAmount >= 1000000) {
      formattedAmount = (absAmount / 1000000).toFixed(3) + 'M';
    } else if (absAmount >= 1000) {
      formattedAmount = (absAmount / 1000).toFixed(3) + 'K';
    } else {
      formattedAmount = absAmount % 1 === 0 ? absAmount.toString() : absAmount.toFixed(3);
    }

    if (formattedAmount.includes('.')) {
      formattedAmount = formattedAmount.replace(/\.?0+$/, '');
    }

    const sign = isNegative ? '-' : '';
    return `${sign}$${formattedAmount}`;
  };

  const getRiskColor = (riskLevel: string) => {
    if (riskLevel.includes('Conservative')) return '#4caf50';
    if (riskLevel.includes('Moderate')) return '#ff9800';
    return '#f44336';
  };

  const handleCardClick = (assetType: 'incomeUnits' | 'growthUnits', available: boolean) => {
    if (!available) return;
    setSelectedAssetType(assetType);
  };

  const handleBackToCards = () => {
    setSelectedAssetType(null);
    // Clear location state when going back
    navigate('/fundManagementInvestment/sell-assets', { replace: true, state: {} });
  };

  const totalPortfolioValue = assetTypeData.reduce((sum, asset) => sum + asset.totalValue, 0);

  // If an asset type is selected, show the corresponding form
  if (selectedAssetType === 'incomeUnits') {
    return (
      <SellIncomeAssets
        onBack={handleBackToCards}
        prefilledAsset={locationState?.prefilledAsset}
        fromAssetList={locationState?.fromAssetList}
      />
    );
  }

  if (selectedAssetType === 'growthUnits') {
    return (
      <SellGrowthAssets
        onBack={handleBackToCards}
        prefilledAsset={locationState?.prefilledAsset}
        fromAssetList={locationState?.fromAssetList}
      />
    );
  }

  // Otherwise show the cards selection view
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          Sell Assets
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select asset type to create a sell request
        </Typography>
      </Box>

      {/* Asset Type Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
          Choose Asset Type
        </Typography>

        <Grid container spacing={3}>
          {assetTypeData.map((assetClass) => (
            <Grid item xs={12} md={6} key={assetClass.id}>
              <Card
                sx={{
                  height: '100%',
                  position: 'relative',
                  cursor: assetClass.available ? 'pointer' : 'not-allowed',
                  opacity: assetClass.available ? 1 : 0.6,
                  transition: 'all 0.3s ease',
                  '&:hover': assetClass.available
                    ? {
                        transform: 'translateY(-8px)',
                        boxShadow: 6
                      }
                    : {}
                }}
              >
                <CardActionArea
                  onClick={() => handleCardClick(assetClass.type, assetClass.available)}
                  disabled={!assetClass.available}
                  sx={{ height: '100%' }}
                >
                  {!assetClass.available && (
                    <Chip
                      label="Coming Soon"
                      color="warning"
                      size="small"
                      sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}
                    />
                  )}

                  {/* Header Gradient */}
                  <Box
                    sx={{
                      background: assetClass.gradient,
                      p: 3,
                      color: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        {assetClass.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                        Total: {formatCurrency(assetClass.totalValue)}
                      </Typography>
                    </Box>
                    <Box sx={{ opacity: 0.8 }}>{assetClass.icon}</Box>
                  </Box>

                  <CardContent>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      {assetClass.description}
                    </Typography>

                    {/* Key Metrics */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-around',
                        mb: 2,
                        p: 2,
                        bgcolor: '#f5f5f5',
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {assetClass.totalAssets}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Assets
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {formatCurrency(assetClass.totalValue)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total Value
                        </Typography>
                      </Box>
                    </Box>

                    {/* Features */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Key Features
                      </Typography>
                      {assetClass.features.map((feature, index) => (
                        <Typography
                          key={index}
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          • {feature}
                        </Typography>
                      ))}
                    </Box>

                    {/* Risk Level and Action Button */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 2
                      }}
                    >
                      <Chip
                        label={assetClass.riskLevel}
                        size="small"
                        sx={{
                          backgroundColor: getRiskColor(assetClass.riskLevel),
                          color: 'white',
                          fontWeight: 500
                        }}
                      />
                      <Button
                        variant="contained"
                        disabled={!assetClass.available}
                        sx={{
                          backgroundColor: '#1976d2',
                          '&:hover': {
                            backgroundColor: '#1565c0'
                          },
                          textTransform: 'none',
                          fontWeight: 500,
                          borderRadius: 2,
                          padding: { xs: '10px', md: '12px' },
                          fontSize: { xs: '0.8rem', md: '0.85rem' }
                        }}
                      >
                        {assetClass.available ? `Sell ${assetClass.name}` : 'Not Available'}
                      </Button>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* No Assets Message */}
      {assetTypeData.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Assets Available to Sell
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You don't have any assets in your portfolio.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/fundManagementInvestment/dashboard')}
            sx={{ textTransform: 'none' }}
          >
            View Dashboard
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default SellAssets;
 